import type { JSX } from 'react'

/**
 * A post heading (or any short markdown run) rendered as INLINE markdown.
 *
 * A section's heading comes from the markdown source, so it can carry inline
 * syntax: `` `usePromise` ``, `**bold**`, `[a link](url)`. Printing that string
 * into JSX shows the syntax — the reader sees literal backticks and asterisks
 * instead of a code element and a bold run — which is why this exists.
 *
 * It handles the INLINE forms only (code, strong, em, links) and is deliberately
 * small: a heading is one line of text, not a document. Everything is escaped
 * through React's own text nodes, so a heading can never inject markup.
 */

/** One piece of a parsed heading: literal text, or a marked-up run. */
type Token =
  | { kind: 'text'; value: string }
  | { kind: 'code'; value: string }
  | { kind: 'strong'; value: string }
  | { kind: 'em'; value: string }
  | { kind: 'link'; value: string; href: string }

/** The inline patterns, in the order they are tried. */
const PATTERNS: Array<{ re: RegExp; build: (m: RegExpExecArray) => Token }> = [
  // `code` — the common case in a technical heading.
  { re: /^`([^`]+)`/, build: (m) => ({ kind: 'code', value: m[1]! }) },
  // **strong** and __strong__
  { re: /^\*\*([^*]+)\*\*/, build: (m) => ({ kind: 'strong', value: m[1]! }) },
  { re: /^__([^_]+)__/, build: (m) => ({ kind: 'strong', value: m[1]! }) },
  // *em* and _em_
  { re: /^\*([^*]+)\*/, build: (m) => ({ kind: 'em', value: m[1]! }) },
  { re: /^_([^_]+)_/, build: (m) => ({ kind: 'em', value: m[1]! }) },
  // [text](href) — and the same with a code span inside the text.
  {
    re: /^\[([^\]]+)\]\(([^)\s]+)\)/,
    build: (m) => ({ kind: 'link', value: m[1]!, href: m[2]! }),
  },
]

/** Split a markdown string into its inline tokens. */
function tokenize(text: string): Token[] {
  const tokens: Token[] = []
  let rest = text
  let literal = ''
  while (rest.length > 0) {
    let matched = false
    for (const { re, build } of PATTERNS) {
      const m = re.exec(rest)
      if (!m) continue
      if (literal) {
        tokens.push({ kind: 'text', value: literal })
        literal = ''
      }
      tokens.push(build(m))
      rest = rest.slice(m[0].length)
      matched = true
      break
    }
    if (!matched) {
      literal += rest[0]
      rest = rest.slice(1)
    }
  }
  if (literal) tokens.push({ kind: 'text', value: literal })
  return tokens
}

/** Render a token's value, honouring a code span nested inside it. */
function value(token: Extract<Token, { kind: 'strong' | 'em' | 'link' }>): JSX.Element[] {
  return tokenize(token.value).map((inner, i) => {
    if (inner.kind === 'code') return <code key={i}>{inner.value}</code>
    if (inner.kind === 'strong') return <strong key={i}>{inner.value}</strong>
    if (inner.kind === 'em') return <em key={i}>{inner.value}</em>
    return <span key={i}>{inner.value}</span>
  })
}

/**
 * Render a short markdown run as inline HTML.
 *
 * @param props.text - The markdown source (a heading's text).
 * @returns The heading's content: code elements, bold runs and links, with the
 *   syntax characters consumed rather than printed.
 */
export function InlineMarkdown({ text }: { text: string }): JSX.Element {
  return (
    <>
      {tokenize(text).map((token, i) => {
        switch (token.kind) {
          case 'code':
            return <code key={i}>{token.value}</code>
          case 'strong':
            return <strong key={i}>{value(token)}</strong>
          case 'em':
            return <em key={i}>{value(token)}</em>
          case 'link':
            return (
              <a key={i} href={token.href}>
                {value(token)}
              </a>
            )
          default:
            return <span key={i}>{token.value}</span>
        }
      })}
    </>
  )
}
